import type {
  AuditLogReadResponse,
  CallLifecycleRequest,
  CallSummary,
  CallsByContactResponse,
  RetrySyncResponse,
  WrapUpRequest,
  WrapUpResponse,
  WrapupDraftPayload,
  ZohoOauthCredentials,
} from '@kunzite/shared-types';

import { useWidgetStore } from '../state/store';

import { refreshOn401 } from './refresh-helper';

/**
 * Per-request token provider. The widget keeps the JWT in memory; refresh on
 * 401 is handled by the shared `refreshOn401` helper, which calls
 * /v1/auth/refresh directly. F4.1: drops the `refresh` field that older
 * TokenProvider implementations exposed as a no-op.
 */
export interface TokenProvider {
  (): Promise<{ jwt: string }>;
}

export class AuthExpiredError extends Error {
  override readonly name = 'AuthExpiredError';
  constructor() {
    super('JWT expired and could not be refreshed');
  }
}

/**
 * F3.1: thrown when the IS responds 401 with body `{ code: 'oauth_revoked' }`.
 * The widget's app-level handler maps this to store.setError so the
 * OAuthRevokedModal can render. Distinct from AuthExpiredError so callers
 * can distinguish "JWT expired" from "tenant's Zoho integration revoked".
 */
export class OAuthRevokedError extends Error {
  override readonly name = 'OAuthRevokedError';
  constructor(message?: string) {
    super(message ?? 'Zoho OAuth has been revoked');
  }
}

/**
 * F3.1: thrown when the IS responds 500 with body `{ code: 'cipher_corrupt' }`.
 * Maps to CipherCorruptModal in the widget.
 */
export class CipherCorruptError extends Error {
  override readonly name = 'CipherCorruptError';
  constructor(message?: string) {
    super(message ?? 'Tenant cipher is corrupt');
  }
}

export class ApiError extends Error {
  override readonly name = 'ApiError';
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message?: string,
  ) {
    super(message ?? `API error ${status}`);
  }
}

/**
 * Parses a non-2xx response body to detect F3.1 typed auth errors.
 * Returns the appropriate Error subclass for `oauth_revoked` / `cipher_corrupt`,
 * or null if the response doesn't match either code. Side effect: dispatches
 * the matching error code to the widget store so the corresponding blocking
 * modal renders (OAuthRevokedModal / CipherCorruptModal).
 */
export async function detectAuthError(res: Response): Promise<OAuthRevokedError | CipherCorruptError | null> {
  const cloned = res.clone();
  let body: unknown;
  try {
    body = await cloned.json();
  } catch {
    return null;
  }
  if (body && typeof body === 'object') {
    const code = (body as { code?: unknown }).code;
    const message = (body as { message?: unknown }).message;
    const messageStr = typeof message === 'string' ? message : undefined;
    if (code === 'oauth_revoked') {
      useWidgetStore.getState().setError({ code: 'oauth_revoked', message: messageStr ?? 'Zoho OAuth has been revoked' });
      return new OAuthRevokedError(messageStr);
    }
    if (code === 'cipher_corrupt') {
      useWidgetStore.getState().setError({ code: 'cipher_corrupt', message: messageStr ?? 'Tenant cipher is corrupt' });
      return new CipherCorruptError(messageStr);
    }
  }
  return null;
}

export interface CallsApiDeps {
  baseUrl: string;
  tokenProvider: TokenProvider;
  onJwtUpdate?: (jwt: string, fresh: { refreshExpiresAt: string; zohoOauth: ZohoOauthCredentials; expiresAt: string }) => void;
  fetch?: typeof globalThis.fetch;
}

export class CallsApiClient {
  constructor(private readonly deps: CallsApiDeps) {}

  reportLifecycle(input: CallLifecycleRequest): Promise<void> {
    return this.send<void>('POST', '/v1/calls/lifecycle', input);
  }

  saveWrapUp(callId: string, input: WrapUpRequest): Promise<WrapUpResponse> {
    return this.send<WrapUpResponse>('POST', `/v1/calls/${encodeURIComponent(callId)}/wrap-up`, input);
  }

  retrySync(callId: string): Promise<RetrySyncResponse> {
    return this.send<RetrySyncResponse>('POST', `/v1/calls/${encodeURIComponent(callId)}/retry-sync`, {});
  }

  findByContact(contactId: string, limit?: number): Promise<CallsByContactResponse> {
    const qs = new URLSearchParams({ contactId });
    if (limit !== undefined) qs.set('limit', String(limit));
    return this.send<CallsByContactResponse>('GET', `/v1/calls/by-contact?${qs.toString()}`);
  }

  findById(callId: string): Promise<CallSummary> {
    return this.send<CallSummary>('GET', `/v1/calls/${encodeURIComponent(callId)}`);
  }

  async saveDraft(callId: string, payload: WrapupDraftPayload): Promise<void> {
    await this.send<void>('PUT', `/v1/calls/${encodeURIComponent(callId)}/draft`, payload);
  }

  async loadDraft(callId: string): Promise<WrapupDraftPayload | null> {
    try {
      return await this.send<WrapupDraftPayload>('GET', `/v1/calls/${encodeURIComponent(callId)}/draft`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  }

  async fetchAuditLog(callId: string): Promise<AuditLogReadResponse> {
    return this.send<AuditLogReadResponse>('GET', `/v1/calls/${encodeURIComponent(callId)}/audit`);
  }

  private async send<T>(method: 'GET' | 'POST' | 'PUT', path: string, body?: unknown): Promise<T> {
    const fetcher = this.deps.fetch ?? globalThis.fetch;
    const tok = await this.deps.tokenProvider();
    return refreshOn401<T>(
      {
        baseUrl: this.deps.baseUrl,
        fetch: fetcher,
        ...(this.deps.onJwtUpdate ? { onJwtUpdate: this.deps.onJwtUpdate } : {}),
      },
      (jwt) => fetcher(`${this.deps.baseUrl}${path}`, this.buildInit(method, jwt, body)),
      (res) => this.parse<T>(res),
      tok.jwt,
    );
  }

  private buildInit(method: 'GET' | 'POST' | 'PUT', jwt: string, body?: unknown): RequestInit {
    const hasBody = body !== undefined && (method === 'POST' || method === 'PUT');
    const init: RequestInit = {
      method,
      headers: {
        Authorization: `Bearer ${jwt}`,
        ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      },
    };
    if (hasBody) {
      init.body = JSON.stringify(body);
    }
    return init;
  }

  private async parse<T>(res: Response): Promise<T> {
    const text = await res.text();
    const parsed: unknown = text ? JSON.parse(text) : undefined;
    if (res.status >= 200 && res.status < 300) return parsed as T;
    throw new ApiError(res.status, parsed);
  }
}
